import "@ovotech/element/reset.css";
import "@ovotech/element/theme.css";

import { theme } from "@ovotech/element";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes, useMatch } from "react-router-dom";
import qs from "qs";
import styled, { ThemeProvider } from "styled-components";
import OVOLogo from "_assets/ovo-logo.svg";
import { StoreProvider, useStore } from "_core/store";
import { Charger, ProductUrls } from "_core/types";
import {
  PersonalDetailsConfirm,
  OffStreetParking,
  Payment,
  PersonalDetails,
  Postcode,
  EmailConfirmation,
  ErrorPage,
  ChargerCustomisation,
  KerboCharge,
} from "_pages";
import "./global.css";
import Mixpanel from "_core/analytics/Mixpanel";

import { datadogRum } from "@datadog/browser-rum";
import { QuoteExpired } from "_pages/QuoteExpired";
import { QuoteRequoted } from "_pages/QuoteRequoted";
import { OptimizelyProvider } from "@optimizely/react-sdk";
import { optimizely } from "_core/utils/optimizely";
import { useCookieConsent } from "_core/analytics/OneTrust";

datadogRum.init({
  applicationId: "105baafc-ba98-442a-9949-e1da40c18580",
  clientToken: "pub6882a02208a1d9631c8d2b33584123bc",
  site: "datadoghq.com",
  service: "ev-charger-installation-ui",
  env:
    process.env.BUILD_ENV === "prod"
      ? "prod"
      : process.env.BUILD_ENV === "nonprod"
      ? "nonprod"
      : "dev",
  // Specify a version number to identify the deployed version of your application in Datadog
  // version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackingConsent: "not-granted",
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
  allowedTracingUrls: [],
});

datadogRum.startSessionReplayRecording();

const StyledApp = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

const StyledLogo = styled(OVOLogo)`
  width: 6rem;
  position: absolute;
  z-index: 40;
  ${({ theme: { customMediaQuery } }) => `
    margin: 2rem 0 2rem 0;
    width: 6rem;
    ${customMediaQuery["medium-and-up"]} {
      margin: 2rem 0 2rem 0;
      display: block;
    };
  `};
`;

const StyledHeaderContainer = styled.div`
  height: 10rem;
  max-width: 100%;
  background: #fff;
`;

const StyledHeader = styled.div`
  height: 10rem;
  max-width: 6rem;
  margin: 0 auto;
  ${({ theme: { customMediaQuery } }) => `
    ${customMediaQuery["medium-and-up"]} {
      max-width: 65%;
    };
  `};
`;

const redirectPaths = [
  ProductUrls.Postcode,
  ProductUrls.OffStreetParking,
  ProductUrls.PersonalDetails,
];

const RedirectIfNoCharger = ({ children }: PropsWithChildren) => {
  const queryParams = new URLSearchParams(window.location.search.split("?")[1]);
  const isMounted = useRef(false);
  const selectedCharger = queryParams.get("charger")?.toLowerCase();
  const { setCharger } = useStore();

  // on the first render we check if the param is present
  // on subsequent renders we double-check we have charger and redirect away

  useEffect(() => {
    if (
      !isMounted.current &&
      redirectPaths.some(
        (path) => window.location.pathname === path,
        redirectPaths
      )
    ) {
      if (
        !selectedCharger ||
        !Object.values(Charger).includes(selectedCharger as Charger)
      ) {
        window.open(
          "https://www.ovoenergy.com/electric-cars/smart-charger/",
          "_self"
        );
      } else {
        isMounted.current = true;
        setCharger(selectedCharger as Charger);
      }
    }
  }, [selectedCharger, setCharger]);

  return <>{children}</>;
};

const App = () => {
  const cookieConsent = useCookieConsent();
  const isPaymentPage = useMatch(ProductUrls.Payment);

  useEffect(() => {
    if (cookieConsent?.performance) {
      datadogRum.setTrackingConsent("granted");
      Mixpanel.opt_in_tracking();
      Mixpanel.set_disable_persistence(false);
    }
  }, [cookieConsent]);

  return (
    <StoreProvider>
      <ThemeProvider theme={theme}>
        <RedirectIfNoCharger>
          <StyledApp>
            <StyledHeaderContainer>
              <StyledHeader>
                <StyledLogo alt="Ovo energy logo" />
              </StyledHeader>
            </StyledHeaderContainer>

            <div className={"Main"}>
              <div className={isPaymentPage ? "ContentPaymentPage" : "Content"}>
                <Routes>
                  <Route path={ProductUrls.Postcode} element={<Postcode />} />
                  <Route
                    path={ProductUrls.OffStreetParking}
                    element={<OffStreetParking />}
                  />
                  <Route
                    path={ProductUrls.KerboCharge}
                    element={<KerboCharge />}
                  />
                  <Route
                    path={ProductUrls.ChargerCustomisation}
                    element={<ChargerCustomisation />}
                  />
                  <Route
                    path={ProductUrls.PersonalDetails}
                    element={<PersonalDetails />}
                  />
                  <Route
                    path={ProductUrls.PersonalDetailsConfirm}
                    element={<PersonalDetailsConfirm />}
                  />
                  <Route
                    path={ProductUrls.EmailConfirmation}
                    element={<EmailConfirmation />}
                  />
                  <Route path={ProductUrls.Payment} element={<Payment />} />
                  <Route
                    path={ProductUrls.Expired}
                    element={<QuoteExpired />}
                  />
                  <Route
                    path={ProductUrls.QuoteRequoted}
                    element={<QuoteRequoted />}
                  />
                  <Route path={ProductUrls.ErrorPage} element={<ErrorPage />} />
                </Routes>
              </div>
            </div>
          </StyledApp>
        </RedirectIfNoCharger>
      </ThemeProvider>
    </StoreProvider>
  );
};

const renderApplication = () => {
  const container = document.getElementById("root");
  const root = createRoot(container!);

  const params = qs.parse(window.location.search);
  if (!params.disableMixpanel) {
    Mixpanel.init(
      `${
        process.env.BUILD_ENV === "prod"
          ? process.env.PROD_MIXPANEL_TOKEN
          : process.env.NONPROD_MIXPANEL_TOKEN
      }`
    );
  } else {
    // We intentionally don't want mixpanel, don't get upset if we can't find it
    Mixpanel.disabled = true;
  }

  root.render(
    <OptimizelyProvider optimizely={optimizely} timeout={500}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </OptimizelyProvider>
  );
};

renderApplication();
